import axios from 'axios';

// Dinamik API URL - Runtime'da belirlenir (build-time değil!)
const getApiBaseUrl = () => {
  // Development için environment variable kullan
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Production: nginx proxy kullan (aynı origin)
  if (typeof window !== 'undefined') {
    // localhost değilse production deployment'tır, nginx proxy kullan
    if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      return '/api'; // Relative URL - nginx proxy'ye gider
    }
    
    // Development: backend'e direkt bağlan
    return `http://${window.location.hostname}:5001/api`;
  }
  
  // SSR fallback
  return 'http://localhost:5001/api';
};

const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000, // 30 saniye (AsistPro API'ye bağlanma için yeterli süre)
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  // Her request'te baseURL'i güncelle (hostname değişebilir)
  config.baseURL = getApiBaseUrl();
  
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle response errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('isAdminLoggedIn');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Products API
export const productsAPI = {
  getAll: () => apiClient.get('/products'),
  getByType: (type: string) => apiClient.get(`/products/${type}`),
  getSunbedAvailability: (date: string) => apiClient.get(`/products/sunbed/availability/${date}`),
  getPublicSetting: (key: string) => apiClient.get(`/products/settings/${key}`)
};

// Bookings API
export const bookingsAPI = {
  create: (data: any) => apiClient.post('/bookings', data),
  getById: (id: string) => apiClient.get(`/bookings/${id}`),
  getByReservationId: (reservationId: string) => apiClient.get(`/bookings/reservation/${reservationId}`),
  getByDateRange: (start: string, end: string) => apiClient.get(`/bookings/date-range/${start}/${end}`),
  lookupReservation: (reservationId: string, email: string) => 
    apiClient.post('/bookings/lookup', { reservationId, email }),
  lookupByEmail: (email: string, phone: string) => 
    apiClient.post('/bookings/lookup-by-email', { email, phone }),
  
  // AsistPro pending rezervasyon oluştur
  // Müşteri ödeme adımlarını tamamladığında (success/fail sayfasına geldiğinde) çağrılır
  // Bu sayede callback gelmeden önce odayı AsistPro'da rezerve edebiliriz
  createPendingReservation: (reservationId: string) => 
    apiClient.post('/bookings/create-pending-reservation', { reservation_id: reservationId }),

  // Pending bir rezervasyonu iptal eder ve şezlong bölge hold'unu serbest bırakır.
  // Müşteri ödeme modalını kapatınca / geri çıkınca çağrılır.
  cancelPending: (reservationId: string) =>
    apiClient.post(`/bookings/${reservationId}/cancel-pending`)
};

// Admin API
export const adminAPI = {
  // Generic methods for new endpoints
  get: (url: string) => apiClient.get(url),
  post: (url: string, data?: any) => apiClient.post(url, data),
  put: (url: string, data?: any) => apiClient.put(url, data),
  patch: (url: string, data?: any) => apiClient.patch(url, data),
  delete: (url: string) => apiClient.delete(url),
  
  login: (username: string, password: string) => 
    apiClient.post('/admin/login', { username, password }),
  
  getBookings: (filters?: any) => 
    apiClient.get('/admin/bookings', { params: filters }),
  
  updateBookingStatus: (id: string, status: string, reason?: string) => 
    apiClient.patch(`/admin/bookings/${id}/status`, { status, reason }),
  
  cancelBooking: (id: string, reason?: string) => 
    apiClient.patch(`/admin/bookings/${id}/status`, { status: 'cancelled', reason }),

  manualConfirmBooking: (id: string) =>
    apiClient.post(`/admin/bookings/${id}/manual-confirm`),
  
  deleteBooking: (id: string) => 
    apiClient.delete(`/admin/bookings/${id}`),

  createInvoice: (id: string) =>
    apiClient.post(`/admin/bookings/${id}/create-invoice`),

  retryOfficialize: (id: string) =>
    apiClient.post(`/admin/bookings/${id}/retry-officialize`),
  
  updateProduct: (type: string, data: any) => 
    apiClient.put(`/admin/products/${type}`, data),
  
  getSettings: (key?: string) => 
    apiClient.get(`/admin/settings${key ? `/${key}` : ''}`),
  
  updateSettings: (key: string, value: any, description?: string) => 
    apiClient.put(`/admin/settings/${key}`, { value, description }),
  
  // General settings (hotel info, contact details)
  getGeneralSettings: () => 
    apiClient.get('/admin/settings/general'),
  
  updateGeneralSettings: (data: {
    hotel_name: string;
    contact_phone: string;
    contact_email: string;
    address: string;
    tax_rates: {
      kdv_rate: number;
      accommodation_tax_rate: number;
    };
    webchat?: {
      enabled: boolean;
      script_url: string;
      channel_id: string;
    };
  }) => apiClient.put('/admin/settings/general', data)
  ,

  // TGA Integration (AsistPro proxy)
  getTgaConfig: () => apiClient.get('/admin/tga/config'),
  getTgaSettings: () => apiClient.get('/admin/tga/settings'),
  updateTgaSettings: (data: {
    base_url: string;
    client_id: string;
    client_secret?: string;
    property_id?: string;
    api_contract_version?: string;
  }) => apiClient.put('/admin/tga/settings', data),
  issueTgaToken: (data?: { scopes?: string[]; ip_allowlist?: string[]; note?: string }) =>
    apiClient.post('/admin/tga/auth/token', data || {}),
  refreshTgaToken: () => apiClient.post('/admin/tga/auth/refresh'),
  getTgaStatus: (propertyId?: string) =>
    apiClient.get(propertyId ? `/admin/tga/status/${propertyId}` : '/admin/tga/status'),
  retryTga: (propertyId?: string) =>
    apiClient.post(propertyId ? `/admin/tga/retry/${propertyId}` : '/admin/tga/retry'),
  getTgaBackfillStatus: (jobId: string) => apiClient.get(`/admin/tga/backfill-status/${jobId}`),
  pauseTgaBackfill: (jobId: string) => apiClient.post(`/admin/tga/backfill/jobs/${jobId}/pause`),
  resumeTgaBackfill: (jobId: string) => apiClient.post(`/admin/tga/backfill/jobs/${jobId}/resume`),
  rotateTgaSecret: (propertyId: string | undefined, newApiKey: string) =>
    apiClient.post(propertyId ? `/admin/tga/secret/rotate/${propertyId}` : '/admin/tga/secret/rotate', {
      new_api_key: newApiKey
    }),
  getTgaComprehensiveInfo: () => apiClient.get('/admin/tga/info'),
  getTgaLogs: (propertyId?: string, limit = 50) =>
    apiClient.get(propertyId ? `/admin/tga/logs/${propertyId}?limit=${limit}` : `/admin/tga/logs?limit=${limit}`),
  getTgaAlerts: (propertyId?: string) =>
    apiClient.get(propertyId ? `/admin/tga/alerts/${propertyId}` : '/admin/tga/alerts')
};

// Public settings API (no auth required)
export const publicAPI = {
  getGeneralSettings: () => apiClient.get('/products/settings/general'),
  getBlockedDates: () => apiClient.get('/products/blocked-dates'),
};

// Homepage API (public read, admin write)
export const homepageAPI = {
  getContent: () => apiClient.get('/homepage'),
  updateAbout: (data: { title: string; paragraph1: string; paragraph2: string }) =>
    apiClient.put('/homepage/about', data),
  uploadAboutImage: (formData: FormData) =>
    apiClient.post('/homepage/about/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getSections: () => apiClient.get('/homepage/sections'),
  createSection: (data: any) => apiClient.post('/homepage/sections', data),
  updateSection: (id: number, data: any) => apiClient.put(`/homepage/sections/${id}`, data),
  deleteSection: (id: number) => apiClient.delete(`/homepage/sections/${id}`),
  uploadSectionImage: (id: number, formData: FormData) =>
    apiClient.post(`/homepage/sections/${id}/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  reorder: (order: { id: number; sort_order: number }[]) =>
    apiClient.patch('/homepage/sections/reorder', { order }),
  // Header
  updateHeader: (data: { site_name: string; logo_url: string; hero_title: string; hero_subtitle: string }) =>
    apiClient.put('/homepage/header', data),
  uploadLogo: (formData: FormData) =>
    apiClient.post('/homepage/header/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Footer
  updateFooter: (data: { tagline: string; instagram_url: string; facebook_url: string; copyright_text: string }) =>
    apiClient.put('/homepage/footer', data),
  // Policies
  getAllPolicies: () => apiClient.get('/homepage/policies'),
  getPolicy: (key: string) => apiClient.get(`/homepage/policies/${key}`),
  updatePolicy: (key: string, data: { title: string; content: string; is_active?: boolean }) =>
    apiClient.put(`/homepage/policies/${key}`, data),
  // Slides
  getSlides: () => apiClient.get('/homepage/slides'),
  addSlide: (formData: FormData) => apiClient.post('/homepage/slides', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteSlide: (id: number) => apiClient.delete(`/homepage/slides/${id}`),
  reorderSlides: (items: { id: number; sort_order: number }[]) => apiClient.patch('/homepage/slides/reorder', { items }),
};

// Rooms API (proxy to external)
export const roomsAPI = {
  getAvailability: (params: any) => apiClient.get('/rooms/availability', { params }),
  getAvailabilityWindow: (params: any) => apiClient.get('/rooms/availability-window', { params }),
  book: (data: any) => apiClient.post('/bookings/room', data) // Yeni endpoint
};

// Payment API (PayTR)
export const paymentAPI = {
  createPayment: (data: {
    booking_id: number;
    email: string;
    phone: string;
    name: string;
    address: string;
    user_ip: string;
  }) => apiClient.post('/payment/create', data),
  
  getStatus: (bookingId: number) => apiClient.get(`/payment/status/${bookingId}`)
};

export { apiClient };
export default apiClient;
